import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Dialog } from '@angular/cdk/dialog';
import { ROUTES, SearchStateService } from '@common/interfaces';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly searchState = inject(SearchStateService);
  private readonly dialog = inject(Dialog);
  private navigationSubscription?: Subscription;

  private readonly onStorageChange = (event: StorageEvent): void => {
    // Case 1 — logout in another tab: localStorage.clear() emits key: null;
    // explicit token removal emits key: 'token' with no newValue.
    const tokenWasCleared =
      event.key === null ||
      (event.key === 'token' && !event.newValue);

    if (tokenWasCleared) {
      if (this.router.url.startsWith('/signin')) return;
      this.searchState.reset();
      this.router.navigate([ROUTES.SIGN_IN]);
      return;
    }

    // Case 2 — login in another tab: token was set while this tab is on sign-in.
    // userInfo is always written before token in loginUser(), so it is already present.
    const tokenWasSet = event.key === 'token' && !!event.newValue;

    if (tokenWasSet && this.router.url.startsWith('/signin')) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') as string);
        if (!userInfo?.roles?.length) return;

        const redirect =
          userInfo.roles[0] === 'USER'
            ? ROUTES.USER_DASHBOARD
            : ROUTES.ASSET_TEAM_DASHBOARD;

        this.router.navigate([redirect]);
      } catch {
        // userInfo missing or invalid — stay on sign-in
      }
    }
  };

  ngOnInit(): void {
    window.addEventListener('storage', this.onStorageChange);

    // CDK dialogs/drawers attach to a global overlay container outside the
    // routed component tree, so navigating away (link click, browser back,
    // programmatic redirect) while one is open never tears it down — the
    // overlay's default BlockScrollStrategy keeps <html> scroll-locked on
    // whatever page loads next, with no CSS able to override that inline
    // style. Closing every open dialog on the way out removes the lock
    // regardless of which path triggered the navigation.
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => this.dialog.closeAll());
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorageChange);
    this.navigationSubscription?.unsubscribe();
  }
}
